import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  useDecisions,
  useCreateDecision,
  useUpdateDecision,
  useSetDecisionStatus,
  useAiSuggest,
  STATUS_LABEL,
  type Decision,
  type DecisionInput,
} from "../lib/decisions";
import "../styles.w3.css";

/**
 * Decision-Log / Entscheidungs-Protokoll (CompanyBrain Welle 3, BEN-107).
 * Entscheidungen strukturiert erfassen (Titel, Kontext, Optionen, Entscheidung, Begruendung,
 * Entscheider/Rolle, Datum, Status) + verknuepfen (KPI/Prozessschritt/Ressort/WP) + durchsuchen.
 * Lokale KI-Hilfe formuliert einen Vorschlag (grounded, nur Vorschlag).
 * Persistenz Orchestrator (Postgres, tenant+user-scoped).
 */

type StatusFilter = "all" | "offen" | "entschieden";

const EMPTY: DecisionInput = {
  title: "", context: "", options: "", decision: "", rationale: "",
  decider: "", decider_role: "", status: "offen", decision_date: "", links: [],
};

function StatusPill({ status }: { status: string }) {
  return <span className={`w3-pill status-${status}`}>{STATUS_LABEL[status] ?? status}</span>;
}

export default function DecisionsPage() {
  const [params, setParams] = useSearchParams();
  const initialStatus = (params.get("status") as StatusFilter) || "all";
  const [status, setStatus] = useState<StatusFilter>(
    ["all", "offen", "entschieden"].includes(initialStatus) ? initialStatus : "all",
  );
  const [search, setSearch] = useState("");
  const q = useDecisions(status, search);
  const create = useCreateDecision();
  const update = useUpdateDecision();
  const setStatusM = useSetDecisionStatus();
  const ai = useAiSuggest();

  const [editing, setEditing] = useState<null | "new" | string>(null);
  const [form, setForm] = useState<DecisionInput>(EMPTY);

  // Deep-Link: ?new=1&title=&context= (z.B. aus dem gefuehrten Prozessschritt)
  useEffect(() => {
    if (params.get("new") === "1" && editing === null) {
      setForm({
        ...EMPTY,
        title: params.get("title") ?? "",
        context: params.get("context") ?? "",
      });
      setEditing("new");
      params.delete("new"); params.delete("title"); params.delete("context");
      setParams(params, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params]);

  const items = q.data?.items ?? [];
  const stats = q.data?.stats ?? { total: 0, offen: 0, entschieden: 0 };
  const persistent = q.data?.persistent ?? false;

  const current: Decision | undefined = useMemo(
    () => (typeof editing === "string" && editing !== "new" ? items.find((d) => d.id === editing) : undefined),
    [editing, items],
  );

  function openNew() {
    setForm({ ...EMPTY });
    setEditing("new");
  }
  function openEdit(d: Decision) {
    setForm({
      title: d.title, context: d.context, options: d.options, decision: d.decision,
      rationale: d.rationale, decider: d.decider, decider_role: d.decider_role,
      status: d.status, decision_date: d.decision_date, links: d.links, meta: d.meta,
    });
    setEditing(d.id);
  }
  function closeEditor() {
    setEditing(null);
    setForm(EMPTY);
    ai.reset();
  }
  function up(patch: Partial<DecisionInput>) {
    setForm((f) => ({ ...f, ...patch }));
  }
  function onSave() {
    if (!form.title?.trim()) return;
    if (editing === "new") {
      create.mutate(form, { onSuccess: () => closeEditor() });
    } else if (typeof editing === "string") {
      update.mutate({ id: editing, input: form }, { onSuccess: () => closeEditor() });
    }
  }
  function runAi() {
    ai.mutate(
      { title: form.title ?? "", context: form.context ?? "", options: form.options ?? "" },
      {
        onSuccess: (res) => {
          up({
            decision: form.decision?.trim() ? form.decision : res.suggestion,
            rationale: form.rationale?.trim() ? form.rationale : res.rationale,
          });
        },
      },
    );
  }

  const saving = create.isPending || update.isPending;

  return (
    <section className="w3-page" aria-label="Entscheidungs-Log">
      <header className="w3-hero">
        <div>
          <p className="hero-eyebrow">CompanyBrain · Welle 3</p>
          <h1>Entscheidungs-Log</h1>
          <p className="muted">
            Halten Sie wichtige Entscheidungen strukturiert fest: <strong>Kontext, Optionen, Entscheidung,
            Begruendung, Entscheider</strong> und Status. Verknuepfbar mit KPI, Prozessschritt, Ressort oder
            Arbeitspaket &mdash; durchsuchbar und nachvollziehbar.
          </p>
        </div>
        <button className="btn primary" onClick={openNew}>+ Neue Entscheidung</button>
      </header>

      {!persistent && (
        <p className="w3-warn small">
          Hinweis: Persistente Speicherung derzeit nicht aktiv (Datenbank nicht verbunden) &mdash; Eintraege
          wirken nur fuer diese Sitzung.
        </p>
      )}

      <div className="w3-toolbar">
        <div className="w3-tabs" role="tablist" aria-label="Status-Filter">
          {(["all", "offen", "entschieden"] as StatusFilter[]).map((s) => (
            <button key={s} role="tab" aria-selected={status === s}
              className={`w3-tab ${status === s ? "active" : ""}`} onClick={() => setStatus(s)}>
              {s === "all" ? "Alle" : STATUS_LABEL[s]}
              <em>{s === "all" ? stats.total : s === "offen" ? stats.offen : stats.entschieden}</em>
            </button>
          ))}
        </div>
        <input className="w3-search" type="search" placeholder="Entscheidungen durchsuchen …"
          value={search} onChange={(e) => setSearch(e.target.value)} aria-label="Suche" />
      </div>

      {(editing !== null) && (
        <div className="w3-editor" role="region" aria-label="Entscheidung bearbeiten">
          <div className="w3-editor-head">
            <h2>{editing === "new" ? "Neue Entscheidung" : "Entscheidung bearbeiten"}</h2>
            <button className="btn ghost small" onClick={closeEditor}>Schliessen</button>
          </div>
          <div className="w3-form-grid">
            <label className="w3-field w3-col2">
              <span>Titel *</span>
              <input value={form.title ?? ""} onChange={(e) => up({ title: e.target.value })}
                placeholder="Worueber wird entschieden?" />
            </label>
            <label className="w3-field w3-col2">
              <span>Kontext</span>
              <textarea rows={3} value={form.context ?? ""} onChange={(e) => up({ context: e.target.value })}
                placeholder="Ausgangslage, Rahmenbedingungen, relevante Fakten …" />
            </label>
            <label className="w3-field w3-col2">
              <span>Optionen</span>
              <textarea rows={2} value={form.options ?? ""} onChange={(e) => up({ options: e.target.value })}
                placeholder="Welche Handlungsoptionen stehen zur Wahl?" />
            </label>

            <div className="w3-ai-row w3-col2">
              <button className="btn ghost" onClick={runAi} disabled={ai.isPending || !form.title?.trim()}
                title="Lokale KI formuliert aus Kontext & Optionen einen Entscheidungsvorschlag (grounded, nur Vorschlag).">
                {ai.isPending ? "KI denkt …" : "✳ KI-Vorschlag (lokal)"}
              </button>
              {ai.data && (
                <span className="muted small">
                  Vorschlag uebernommen ({ai.data.engine}). Bitte pruefen und anpassen.
                </span>
              )}
            </div>

            <label className="w3-field w3-col2">
              <span>Entscheidung</span>
              <textarea rows={2} value={form.decision ?? ""} onChange={(e) => up({ decision: e.target.value })}
                placeholder="Wie wurde entschieden?" />
            </label>
            <label className="w3-field w3-col2">
              <span>Begruendung</span>
              <textarea rows={2} value={form.rationale ?? ""} onChange={(e) => up({ rationale: e.target.value })}
                placeholder="Warum diese Entscheidung?" />
            </label>
            <label className="w3-field">
              <span>Entscheider/in</span>
              <input value={form.decider ?? ""} onChange={(e) => up({ decider: e.target.value })} placeholder="Name" />
            </label>
            <label className="w3-field">
              <span>Rolle</span>
              <input value={form.decider_role ?? ""} onChange={(e) => up({ decider_role: e.target.value })}
                placeholder="z.B. Inhaberin, CFO, Ressort-Lead" />
            </label>
            <label className="w3-field">
              <span>Datum</span>
              <input type="date" value={form.decision_date ?? ""} onChange={(e) => up({ decision_date: e.target.value })} />
            </label>
            <label className="w3-field">
              <span>Status</span>
              <select value={form.status ?? "offen"} onChange={(e) => up({ status: e.target.value })}>
                <option value="offen">Offen</option>
                <option value="entschieden">Entschieden</option>
              </select>
            </label>
          </div>
          <div className="w3-editor-actions">
            <button className="btn primary" onClick={onSave} disabled={saving || !form.title?.trim()}>
              {saving ? "Speichere …" : "Speichern"}
            </button>
            {current && (
              <button className="btn ghost"
                onClick={() => setStatusM.mutate({ id: current.id, status: current.status === "offen" ? "entschieden" : "offen" })}
                disabled={setStatusM.isPending}>
                {current.status === "offen" ? "Als entschieden markieren" : "Wieder oeffnen"}
              </button>
            )}
          </div>
        </div>
      )}

      <div className="w3-list">
        {q.isLoading && <p className="muted">Entscheidungen werden geladen …</p>}
        {!q.isLoading && items.length === 0 && (
          <div className="w3-empty">
            <p><strong>Noch keine Entscheidungen erfasst.</strong></p>
            <p className="muted small">
              Legen Sie die erste Entscheidung an &mdash; oder uebernehmen Sie Punkte aus dem Meeting-Cockpit
              bzw. dem gefuehrten Prozessschritt („Entscheidung protokollieren“).
            </p>
            <button className="btn primary" onClick={openNew}>+ Neue Entscheidung</button>
          </div>
        )}
        {items.map((d) => (
          <article key={d.id} className={`w3-card dec-card status-${d.status}`} onClick={() => openEdit(d)}
            tabIndex={0} role="button" onKeyDown={(e) => { if (e.key === "Enter") openEdit(d); }}>
            <div className="w3-card-top">
              <h3>{d.title}</h3>
              <StatusPill status={d.status} />
            </div>
            {d.context && <p className="w3-card-ctx muted">{d.context.slice(0, 220)}{d.context.length > 220 ? "…" : ""}</p>}
            {d.decision && <p className="w3-card-decision"><strong>Entscheidung:</strong> {d.decision.slice(0, 180)}</p>}
            <div className="w3-card-meta muted small">
              {d.decider && <span>{d.decider}{d.decider_role ? ` · ${d.decider_role}` : ""}</span>}
              {d.decision_date && <span>· {d.decision_date}</span>}
              {(d.links ?? []).length > 0 && <span>· {d.links.length} Verknuepfung(en)</span>}
            </div>
            {(d.links ?? []).length > 0 && (
              <div className="w3-links">
                {d.links.map((l, i) => (
                  l.route ? <a key={i} href={l.route} className="w3-link-chip" onClick={(e) => e.stopPropagation()}>{l.label ?? l.kind ?? "Link"}</a>
                    : <span key={i} className="w3-link-chip">{l.label ?? l.kind}</span>
                ))}
              </div>
            )}
          </article>
        ))}
      </div>
    </section>
  );
}
